<template>
  <article id="news-detail" class="news-detail" :class="{ 'is-page': front.isContentDetailPage }">
    <nav v-if="front.isContentDetailPage" class="detail-breadcrumb" aria-label="当前位置">
      <span>您当前的位置：</span>
      <button type="button" class="detail-crumb-link" @click="front.goPath('/')">
        <span class="detail-home-ico" aria-hidden="true" v-html="homeIcon" />
        首页
      </button>
      <template v-if="isBranchArticle">
        <span class="detail-crumb-sep">›</span>
        <button type="button" class="detail-crumb-link" @click="goBranchHomeCrumb">{{ branchAssociationName }}</button>
        <span class="detail-crumb-sep">›</span>
        <button type="button" class="detail-crumb-link" @click="goModuleCrumb">{{ breadcrumbModuleLabel }}</button>
      </template>
      <template v-else-if="isPartyArticle">
        <span class="detail-crumb-sep">›</span>
        <button type="button" class="detail-crumb-link" @click="front.goPath('/party')">党建文化</button>
      </template>
      <template v-else-if="serviceTabValue">
        <span class="detail-crumb-sep">›</span>
        <button type="button" class="detail-crumb-link" @click="front.goPath('/services')">学会服务</button>
        <span class="detail-crumb-sep">›</span>
        <button type="button" class="detail-crumb-link" @click="goModuleCrumb">{{ breadcrumbModuleLabel }}</button>
      </template>
      <template v-else>
        <span class="detail-crumb-sep">›</span>
        <button type="button" class="detail-crumb-link" @click="goModuleCrumb">{{ breadcrumbModuleLabel }}</button>
      </template>
      <span class="detail-crumb-sep">›</span>
      <strong class="detail-crumb-current">{{ breadcrumbColumnLabel }}</strong>
    </nav>
    <div class="news-detail-head">
      <div class="news-detail-head-main">
        <span class="detail-type">{{ item.type }}</span>
        <h3>{{ item.title }}</h3>
        <div v-if="front.isContentDetailPage" class="detail-meta">
          <span>日期：{{ displayDate }}</span>
        </div>
        <time v-else>{{ displayDate }}</time>
      </div>
      <button v-if="!front.isContentDetailPage" type="button" class="text-link" @click="front.selectedContent = null">关闭</button>
    </div>
    <img v-if="coverDisplayUrl" :src="coverDisplayUrl" alt="" />
    <div v-if="externalArticleUrl" class="news-external">
      <p>该内容为外部链接，已尝试在新窗口打开。</p>
      <a :href="externalArticleUrl" target="_blank" rel="noopener noreferrer" class="primary-action compact">访问外链</a>
    </div>
    <template v-else>
    <p v-if="item.summary" class="news-summary">{{ item.summary }}</p>
    <div class="news-body" v-html="item.bodyContent || '暂无正文内容'"></div>
    <div v-if="downloadItems.length" class="news-attachments">
      <div class="news-attachments__title">附件下载</div>
      <ul class="news-attachments__list">
        <li v-for="(file, index) in downloadItems" :key="file.id || file.url || index">
          <button
            type="button"
            class="attachment-link"
            :title="attachmentTitle(file)"
            @click="downloadAttachment(file)"
          >
            <span class="attachment-doc-icon" aria-hidden="true"></span>
            <span class="attachment-link-text">{{ attachmentTitle(file) }}</span>
          </button>
        </li>
      </ul>
    </div>
    </template>
    <nav v-if="front.isContentDetailPage" class="article-pager" aria-label="文章翻页">
      <button type="button" :disabled="!previousArticle" @click="goArticle(previousArticle)">
        <span>上一篇</span>
        <strong>{{ previousArticle ? previousArticle.title : '没有了' }}</strong>
      </button>
      <button type="button" :disabled="!nextArticle" @click="goArticle(nextArticle)">
        <span>下一篇</span>
        <strong>{{ nextArticle ? nextArticle.title : '没有了' }}</strong>
      </button>
    </nav>
  </article>
</template>

<script>
import { listPublicCmsEntries, unwrapPublicPage } from '@/api/paleo'
import { branchContentPath, branchSitePath, resolveBranchSection } from '@/data/branchSite'
import { findScienceTabByEntry, matchesScienceTab, scienceSiblingQuery } from '@/utils/scienceCategories'

const homeIcon = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-5v-6H10v6H5a1 1 0 0 1-1-1v-9.5Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/></svg>'

const HOME_PORTAL_PATHS = {
  publications: '/publications',
  dynamics: '/news-publish?board=society_dynamics',
  progress: '/news-publish?board=work_progress',
  society_dynamics: '/news-publish?board=society_dynamics',
  work_progress: '/news-publish?board=work_progress'
}

const PUBLISH_BOARD_LABELS = {
  meeting_notice: '会议通知',
  party_public: '党务公开',
  important_news: '重要新闻',
  society_dynamics: '学会动态',
  work_progress: '工作进展'
}

export default {
  name: 'ContentDetailPanel',
  inject: ['front'],
  data() {
    return {
      homeIcon,
      siblingList: [],
      siblingLoadSeq: 0
    }
  },
  computed: {
    item() {
      return this.front.selectedContent || {}
    },
    coverDisplayUrl() {
      const raw = this.item.coverImageUrl || this.item.coverUrl
      if (!raw) {
        return ''
      }
      return this.front.resolvePublicDisplayUrl
        ? this.front.resolvePublicDisplayUrl(raw)
        : this.front.resolveFileUrl(raw)
    },
    externalArticleUrl() {
      const url = String(this.item.linkUrl || '').trim()
      return /^https?:\/\//i.test(url) ? url : ''
    },
    downloadItems() {
      const list = Array.isArray(this.item.attachments)
        ? this.item.attachments.filter(item => item && item.url)
        : []
      if (list.length) {
        return list
      }
      // 兼容旧数据：仅有 fileUrl / originalFile，且不是「整篇外链」
      const url = this.item.originalFileUrl || this.item.fileUrl
      if (!url || (this.item.linkUrl && url === this.item.linkUrl)) {
        return []
      }
      return [{
        id: 'primary',
        name: this.item.originalFileName || this.front.fileNameFromUrl(url),
        url
      }]
    },
    displayDate() {
      const raw = String(this.item.fullDate || this.item.date || '').trim()
      return raw ? raw.slice(0, 10) : '待发布'
    },
    isPartyArticle() {
      return this.item.moduleCode === 'party' || this.item.scope === 'party'
    },
    isBranchArticle() {
      return !!(this.$route && this.$route.params && this.$route.params.branchId)
    },
    branchId() {
      return this.isBranchArticle ? this.$route.params.branchId : ''
    },
    branchSection() {
      const routeSection = (this.$route.meta && this.$route.meta.branchSection)
        || (this.$route.params && this.$route.params.section)
      return resolveBranchSection(this.isBranchArticle ? routeSection : '')
    },
    branchAssociationName() {
      const list = (this.front && this.front.bindableAssociations) || []
      const association = list.find(item => String(item.associationId) === String(this.branchId))
      return (association && association.associationName) || this.item.associationName || '分会子站'
    },
    /** 学会服务下的专题栏目：国际交流 / 科学传播 / 科技奖励 */
    serviceTabValue() {
      const map = {
        international: 'international',
        science: 'science',
        'tech-rewards': 'rewards',
        awards: 'rewards'
      }
      return map[this.item.moduleCode] || ''
    },
    breadcrumbModuleLabel() {
      if (this.isBranchArticle) {
        return this.branchSection.title
      }
      if (this.isPartyArticle) {
        return '党建文化'
      }
      if (this.serviceTabValue === 'rewards') {
        return '科技奖励'
      }
      if (this.item.moduleCode === 'personnel') {
        return '学会简介'
      }
      if (this.item.moduleCode === 'timeline' || this.item.moduleCode === 'history') {
        return '学会沿革'
      }
      if (this.item.moduleCode === 'home_portal') {
        return this.front.contentModuleLabel(this.item)
      }
      if (this.item.moduleCode === 'banners') {
        return '首页轮播'
      }
      return this.item.moduleLabel || this.item.type || '内容中心'
    },
    breadcrumbColumnLabel() {
      if (this.isPartyArticle) {
        return this.item.category || this.item.title || '党建内容'
      }
      if (this.item.moduleCode === 'timeline' || this.item.moduleCode === 'history') {
        return this.item.title || '文章详情'
      }
      if (this.item.moduleCode === 'home_portal') {
        return this.item.title || this.front.contentModuleLabel(this.item)
      }
      if (this.item.moduleCode === 'publish') {
        return PUBLISH_BOARD_LABELS[this.item.columnCode] || this.item.type || this.item.title || '通知要闻'
      }
      if (this.item.moduleCode === 'banners') {
        return this.item.title || '轮播文章'
      }
      if (this.item.moduleCode === 'science') {
        return this.item.summary || this.item.category || this.item.title || '科学传播'
      }
      return this.item.category || this.item.type || '文章详情'
    },
    siblingArticles() {
      return this.siblingList
    },
    currentArticleIndex() {
      const currentId = this.item.entryId
      if (!currentId) return -1
      return this.siblingArticles.findIndex(article => String(article.entryId) === String(currentId))
    },
    previousArticle() {
      if (this.currentArticleIndex <= 0) {
        return null
      }
      return this.siblingArticles[this.currentArticleIndex - 1]
    },
    nextArticle() {
      if (this.currentArticleIndex < 0 || this.currentArticleIndex >= this.siblingArticles.length - 1) {
        return null
      }
      return this.siblingArticles[this.currentArticleIndex + 1]
    }
  },
  watch: {
    'item.entryId': {
      immediate: true,
      handler(entryId) {
        if (entryId && this.front.isContentDetailPage) {
          this.loadSiblingArticles()
        } else {
          this.siblingList = []
        }
      }
    }
  },
  methods: {
    buildSiblingQuery() {
      const moduleCode = this.item.moduleCode
      if (!moduleCode || !this.item.entryId) return null
      if (moduleCode === 'science') {
        return scienceSiblingQuery(this.item)
      }
      const params = {
        moduleCode,
        pageNum: 1,
        pageSize: 200
      }
      if (this.isBranchArticle) {
        params.associationId = this.branchId
      }
      const columnCode = String(this.item.columnCode || '').trim()
      // 仅共享栏目码的模块按栏目翻页；规章/国际交流等一篇一码不能带 columnCode
      if (columnCode && this.useColumnInSiblingQuery(moduleCode)) {
        params.columnCode = columnCode
      }
      return params
    },
    useColumnInSiblingQuery(moduleCode) {
      return ['publish', 'party', 'home_portal', 'pages'].indexOf(moduleCode) >= 0
    },
    sortSiblings(rows) {
      const isTimeline = this.item.moduleCode === 'timeline' || this.item.moduleCode === 'history'
      const isBanner = this.item.moduleCode === 'banners'
      if (this.item.moduleCode === 'party') {
        const pinnedValues = ['1', 'Y', true, 'true']
        return (rows || []).slice().sort((a, b) => {
          const pinDiff = Number(pinnedValues.includes(b.pinned)) - Number(pinnedValues.includes(a.pinned))
          if (pinDiff !== 0) return pinDiff
          return String(b.publishTime || b.createTime || '').localeCompare(
            String(a.publishTime || a.createTime || '')
          )
        })
      }
      return (rows || []).slice().sort((a, b) => {
        if (isTimeline || isBanner) {
          const sortCmp = (a.sortOrder || 0) - (b.sortOrder || 0)
          if (sortCmp !== 0) return sortCmp
        }
        const dateCmp = String(b.publishTime || b.createTime || '').localeCompare(
          String(a.publishTime || a.createTime || '')
        )
        if (dateCmp !== 0) return dateCmp
        return (a.sortOrder || 0) - (b.sortOrder || 0)
      })
    },
    async loadSiblingArticles() {
      const seq = ++this.siblingLoadSeq
      const params = this.buildSiblingQuery()
      if (!params) {
        this.siblingList = []
        return
      }
      try {
        const result = await listPublicCmsEntries(params)
        if (seq !== this.siblingLoadSeq) return
        const rows = (unwrapPublicPage(result).rows || []).map(row => this.front.mapCmsEntry(row))
        if (this.item.moduleCode === 'science') {
          const tab = findScienceTabByEntry(this.item)
          this.siblingList = this.sortSiblings(tab ? rows.filter(row => matchesScienceTab(row, tab)) : rows)
        } else {
          this.siblingList = this.sortSiblings(rows)
        }
      } catch (error) {
        if (seq !== this.siblingLoadSeq) return
        this.siblingList = []
      }
    },
    downloadAttachment(file) {
      this.front.downloadContentOriginal({
        ...this.item,
        fileUrl: file.url,
        originalFileUrl: file.url,
        originalFileName: file.originalName || file.name || this.front.fileNameFromUrl(file.url)
      })
    },
    attachmentTitle(file) {
      const name = String((file && file.name) || '').trim()
      const original = String((file && file.originalName) || '').trim()
      if (name && original && original !== name) {
        return `${name}（${original}）`
      }
      return name || original || this.front.fileNameFromUrl(file && file.url)
    },
    goModuleCrumb() {
      if (this.isBranchArticle) {
        this.front.goPath(branchSitePath(this.branchId, this.branchSection.id))
        return
      }
      if (this.isPartyArticle) {
        this.front.goPath(this.item.modulePath || '/party')
        return
      }
      if (this.item.moduleCode === 'science') {
        const matched = findScienceTabByEntry(this.item)
        this.front.goServicesTab('science', { cat: (matched && matched.key) || 'base' })
        return
      }
      if (this.serviceTabValue) {
        this.front.goServicesTab(this.serviceTabValue)
        return
      }
      if (this.item.moduleCode === 'personnel') {
        this.front.goPath('/intro')
        return
      }
      if (this.item.moduleCode === 'timeline' || this.item.moduleCode === 'history') {
        this.front.goPath('/history')
        return
      }
      if (this.item.moduleCode === 'home_portal') {
        const path = HOME_PORTAL_PATHS[this.item.columnCode] || '/'
        this.front.goPath(path)
        return
      }
      if (this.item.moduleCode === 'banners') {
        this.front.goPath('/')
        return
      }
      if (this.item.moduleCode === 'publish') {
        const board = String(this.item.columnCode || '').trim()
        this.front.goPath(board ? `/news-publish?board=${board}` : '/news-publish')
        return
      }
      this.front.goPath(this.item.modulePath || '/news-publish')
    },
    goBranchHomeCrumb() {
      this.front.goPath(branchSitePath(this.branchId, 'home'))
    },
    goArticle(article) {
      if (article && article.entryId) {
        const path = this.isBranchArticle
          ? branchContentPath(this.branchId, this.branchSection.id, article.entryId)
          : `/content/${article.entryId}`
        this.front.goPath(path)
      }
    }
  }
}
</script>

<style scoped>
.news-detail.is-page {
  margin-top: 0;
  padding: 28px 40px 40px;
  border: 1px solid var(--border);
  border-top: 3px solid var(--gold);
  border-radius: var(--front-radius);
  background: #fff;
  box-shadow: var(--shadow-soft, 0 8px 24px rgba(0, 43, 73, 0.06));
}

.detail-breadcrumb {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin: 0 0 22px;
  padding: 0 0 16px;
  border: 0;
  border-bottom: 1px solid var(--border);
  background: transparent;
  color: #5f6670;
  font-size: 13px;
  line-height: 1.5;
}

.detail-crumb-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 0;
  padding: 0;
  background: transparent;
  color: #5f6670;
  cursor: pointer;
  font: inherit;
  font-weight: 500;
}

.detail-crumb-link:hover {
  color: var(--primary);
}

.detail-home-ico {
  display: inline-flex;
  align-items: center;
  line-height: 0;
}

.detail-home-ico >>> svg {
  display: block;
}

.detail-crumb-sep {
  color: #9aa3ad;
  font-weight: 500;
}

.detail-crumb-current {
  color: var(--party);
  font-weight: 600;
}

.news-detail-head-main {
  min-width: 0;
  width: 100%;
}

.detail-type {
  display: inline-flex;
  min-height: 24px;
  align-items: center;
  border-radius: 999px;
  background: var(--gold-soft);
  color: var(--primary);
  padding: 0 10px;
  font-size: 12px;
  font-weight: 800;
}

.detail-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 10px;
  margin-top: 14px;
  color: var(--subtle);
  font-size: 13px;
  line-height: 1.5;
}

.detail-meta-sep {
  color: #c3c8ce;
}

.news-detail.is-page .news-detail-head h3 {
  margin: 14px 0 0;
  max-width: none;
  font-size: clamp(24px, 2.4vw, 32px);
  line-height: 1.35;
}

.news-detail.is-page .news-summary,
.news-detail.is-page .news-body {
  max-width: none;
  font-size: 16px;
}

.news-detail.is-page .news-body {
  min-height: 120px;
  color: var(--text);
  line-height: 2;
}

.news-external {
  display: grid;
  gap: 12px;
  margin: 8px 0 12px;
  padding: 16px 18px;
  border: 1px solid var(--border);
  border-radius: var(--front-radius);
  background: #f7f9fc;
  color: var(--subtle);
  font-size: 14px;
  line-height: 1.7;
}

.news-external p {
  margin: 0;
}

.news-external .primary-action {
  justify-self: start;
}

.article-pager {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-top: 36px;
  padding-top: 22px;
  border-top: 1px solid var(--border);
}

.article-pager button {
  display: grid;
  gap: 6px;
  min-height: 0;
  padding: 14px 16px;
  border: 1px solid var(--border);
  border-radius: var(--front-radius);
  background: #f7f9fc;
  color: var(--ink);
  cursor: pointer;
  text-align: left;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.article-pager button:last-child {
  text-align: right;
}

.article-pager button:hover:not(:disabled) {
  border-color: rgba(0, 43, 73, 0.28);
  background: #fff;
}

.article-pager button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.article-pager span {
  color: var(--primary);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.article-pager strong {
  overflow: hidden;
  color: var(--text);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 720px) {
  .news-detail.is-page {
    padding: 20px 18px 28px;
  }

  .article-pager {
    grid-template-columns: 1fr;
  }

  .article-pager button:last-child {
    text-align: left;
  }
}
</style>
